import { useEffect, useRef } from "react";

/**
 * MatrixCursor
 * - Soft violet glow that smoothly follows the cursor (eased translate).
 * - Trail of falling Matrix-style glyphs that spawn at low frequency
 *   while the user moves the cursor, then fade and drop.
 * - Native cursor stays as-is. Effect is pointer-events: none.
 * - Disabled on coarse-pointer devices (touch).
 */
export const MatrixCursor = () => {
  const layerRef = useRef(null);
  const glowRef = useRef(null);
  const stateRef = useRef({
    x: -100,
    y: -100,
    tx: -100,
    ty: -100,
    lastSpawn: 0,
    raf: 0,
    pool: [],
    poolIdx: 0,
    moving: false,
    moveTimer: null,
  });

  useEffect(() => {
    if (typeof window === "undefined") return;
    // Disable on touch / coarse pointer devices
    if (
      window.matchMedia &&
      window.matchMedia("(pointer: coarse)").matches
    ) {
      return;
    }

    const layer = layerRef.current;
    const glow = glowRef.current;
    if (!layer || !glow) return;

    // Pre-create a pool of glyph spans for recycling
    const POOL_SIZE = 22;
    const GLYPHS =
      "ｱｲｳｴｵｶｷｸｹｺｻｼｽｾｿﾀﾁﾂﾃﾄﾅﾆﾇﾈﾉﾊﾋﾌﾍﾎﾏﾐﾑﾒﾓﾔﾕﾖﾗﾘﾙﾚﾛﾜﾝ0123456789=*<>+/\\";

    const pool = [];
    for (let i = 0; i < POOL_SIZE; i++) {
      const span = document.createElement("span");
      span.className = "rm-matrix-glyph";
      span.textContent = "0";
      layer.appendChild(span);
      pool.push(span);
    }
    stateRef.current.pool = pool;

    const spawnGlyph = () => {
      const s = stateRef.current;
      const span = s.pool[s.poolIdx % POOL_SIZE];
      s.poolIdx += 1;
      // Reset any in-flight animation
      span.style.animation = "none";
      // Force reflow so animation can restart
      // eslint-disable-next-line no-unused-expressions
      span.offsetHeight;
      span.textContent = GLYPHS[Math.floor(Math.random() * GLYPHS.length)];
      const jitterX = (Math.random() - 0.5) * 22;
      const jitterY = (Math.random() - 0.5) * 8;
      span.style.left = `${s.x + jitterX}px`;
      span.style.top = `${s.y + jitterY}px`;
      span.style.animation = "rm-matrix-fall 900ms ease-out forwards";
    };

    const onMove = (e) => {
      const s = stateRef.current;
      s.tx = e.clientX;
      s.ty = e.clientY;
      s.moving = true;
      if (s.moveTimer) clearTimeout(s.moveTimer);
      s.moveTimer = setTimeout(() => {
        s.moving = false;
      }, 80);
    };

    const tick = (t) => {
      const s = stateRef.current;
      // Smoothly ease cursor position toward target
      s.x += (s.tx - s.x) * 0.22;
      s.y += (s.ty - s.y) * 0.22;
      glow.style.transform = `translate3d(${s.x - 60}px, ${s.y - 60}px, 0)`;

      // Spawn glyphs only while moving, throttled
      if (s.moving && t - s.lastSpawn > 110) {
        spawnGlyph();
        s.lastSpawn = t;
      }
      s.raf = requestAnimationFrame(tick);
    };

    window.addEventListener("mousemove", onMove, { passive: true });
    stateRef.current.raf = requestAnimationFrame(tick);

    return () => {
      window.removeEventListener("mousemove", onMove);
      if (stateRef.current.raf) cancelAnimationFrame(stateRef.current.raf);
      if (stateRef.current.moveTimer) clearTimeout(stateRef.current.moveTimer);
      pool.forEach((s) => {
        if (s.parentNode) s.parentNode.removeChild(s);
      });
    };
  }, []);

  return (
    <div
      ref={layerRef}
      className="rm-matrix-layer"
      aria-hidden="true"
      data-testid="matrix-cursor-layer"
    >
      <div ref={glowRef} className="rm-matrix-glow" />
    </div>
  );
};
